import { isPlatformBrowser } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { TrackingConsentStatus } from './interface';
import { TrackingConsent } from './tracking-consent.service';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';
import type { CustomTrackingEvent, TrackingEventData } from './tracking.model';

// list of facebook standard events
// https://developers.facebook.com/docs/facebook-pixel/reference#standard-events
export enum FacebookStandardEvents {
  PURCHASE = 'Purchase',
  COMPLETE_REGISTRATION = 'CompleteRegistration',
  INITIATE_CHECKOUT = 'InitiateCheckout',
  ADD_PAYMENT_INFO = 'AddPaymentInfo',
  CONTACT = 'Contact',
  ADD_TO_CART = 'AddToCart',
}

export interface FacebookAddToCartPayload {
  value?: number;
  currency?: string;
  content_ids: readonly string[];
  content_type: string;
}

// Tracking event names (as passed to `trackCustomEvent`) that should also
// trigger a Facebook Pixel `AddToCart` standard event.
const ADD_TO_CART_TRIGGER_EVENTS: ReadonlySet<string> = new Set([
  'Post Contest',
  'Post Hire Me Project',
  'Post Non Hire Me Project',
]);

@Injectable({
  providedIn: 'root',
})
export class FacebookPixelTracking {
  private ngZone = inject(NgZone);
  private trackingConsent = inject(TrackingConsent);
  private config = inject<TrackingConfig>(TRACKING_CONFIG);
  private platformId = inject(PLATFORM_ID);

  private isEnabled = false;
  private isInitialized = false;

  async init(): Promise<void> {
    const trackingConsentStatus =
      await this.trackingConsent.getThirdPartyStatus();
    if (
      this.config.facebookPixelId &&
      isPlatformBrowser(this.platformId) &&
      trackingConsentStatus === TrackingConsentStatus.AUTHORIZED
    ) {
      this.isEnabled = true;
    }
    this.isInitialized = true;
  }

  async trackPageView(): Promise<void> {
    await this.init();
    if (!this.isEnabled) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.loadTrackingSnippet();
      window.fbq('track', 'PageView');
    });
  }

  async trackCustomEvent(
    payload: CustomTrackingEvent & TrackingEventData,
  ): Promise<void> {
    if (!this.isInitialized) {
      await this.init();
    }

    if (!this.isEnabled) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.loadTrackingSnippet();
      const trackingType = payload.facebookEventName ? 'track' : 'trackCustom';
      const trackingName = payload.facebookEventName || payload.name;
      window.fbq(trackingType, trackingName, {
        ...{
          content_category: payload.section,
        },
        ...(payload.extra_params || {}),
      });

      if (ADD_TO_CART_TRIGGER_EVENTS.has(payload.name)) {
        this.fireAddToCart(payload);
      }
    });
  }

  private fireAddToCart(
    payload: CustomTrackingEvent & TrackingEventData,
  ): void {
    const extra = (payload.extra_params || {}) as Record<string, unknown>;
    const addToCartPayload: FacebookAddToCartPayload = {
      content_ids: Array.isArray(extra.content_ids)
        ? (extra.content_ids as readonly string[])
        : [payload.name],
      content_type:
        typeof extra.content_type === 'string' ? extra.content_type : 'product',
    };
    window.fbq('track', FacebookStandardEvents.ADD_TO_CART, addToCartPayload);
  }

  private loadTrackingSnippet(): void {
    if (window.fbq) {
      return;
    }
    window.fbq = function fbq(): void {
      if (window.fbq.callMethod) {
        // eslint-disable-next-line prefer-rest-params, prefer-spread
        window.fbq.callMethod.apply(window.fbq, arguments);
      } else {
        // eslint-disable-next-line prefer-rest-params
        window.fbq.queue.push(arguments);
      }
    };
    if (!window._fbq) {
      window._fbq = window.fbq;
    }
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    window.fbq.queue = [];

    const e = document.createElement('script');
    e.async = true;
    e.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const s = document.getElementsByTagName('script')[0];
    (s.parentNode as Node).insertBefore(e, s);

    window.fbq('dataProcessingOptions', ['LDU'], 0, 0); // Enable LDU and have Meta perform geolocation
    window.fbq('init', this.config.facebookPixelId);
  }
}
